import React, { useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../components/Button';
import { ContentView } from '../components/ContentView';
import { emailSelector, environmentSelector, setEmail, setEnvironmentConfig, } from '../redux/appSlice';
import { useRequestPublicAccountDeletionMutation } from '../services/abbiWayApi';
import { applyEnvironmentForLogin, resolveLoginEmailFromInput, } from '../utils/resolveLoginEmailFromInput';
import { reportServerError } from '../utils/reportServerError';
import { theme } from '../theme';
const PUBLIC_SUCCESS_MESSAGE = 'Deletion request received. If an account exists for that email, we’ll get back to you within a few days.';
/**
 * D4.1 public deletion form (web guest). No login required.
 * Hits the same request → notify → manual process pipeline as in-app D2.
 */
export const RequestAccountDeletionScreen = () => {
    const storedEmail = useSelector(emailSelector);
    const currentEnvironment = useSelector(environmentSelector);
    const [emailTyped, setEmailTyped] = useState(() => storedEmail ?? '');
    const [message, setMessage] = useState(null);
    const dispatch = useDispatch();
    const [requestDeletion, { isLoading }] = useRequestPublicAccountDeletionMutation();
    const submit = async () => {
        const trimmed = emailTyped.trim();
        if (!trimmed) {
            return;
        }
        const { email: loginEmail, environment } = resolveLoginEmailFromInput(emailTyped, currentEnvironment);
        const env = applyEnvironmentForLogin(environment);
        dispatch(setEnvironmentConfig({
            environment: env.environment,
            backendBaseUrl: env.backendBaseUrl,
            frontendBaseUrl: env.frontendBaseUrl,
        }));
        dispatch(setEmail(emailTyped));
        setMessage(null);
        try {
            const result = await requestDeletion({ email: loginEmail }).unwrap();
            setMessage(result?.message || PUBLIC_SUCCESS_MESSAGE);
        }
        catch (err) {
            reportServerError(err);
        }
    };
    return (<ContentView>
      <View style={styles.container}>
        <Text style={styles.intro}>
          Request deletion of your Abbi's Way account. This sends a request
          only — your account is not deleted automatically. Someone will follow
          up within a few days.
        </Text>
        <Text style={styles.label}>Email</Text>
        <TextInput style={styles.input} placeholder="email address" value={emailTyped} onChangeText={setEmailTyped} autoCapitalize="none" keyboardType="email-address" autoComplete="email"/>
        {message ? <Text style={styles.message}>{message}</Text> : null}
        <View style={styles.button}>
          <Button title={isLoading ? 'Sending...' : 'Request deletion'} disabled={!emailTyped.trim().length || isLoading} onPress={submit}/>
        </View>
      </View>
    </ContentView>);
};
const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 24,
        paddingTop: 24,
        maxWidth: 560,
    },
    intro: {
        fontSize: 16,
        lineHeight: 22,
        color: theme.colors.textBody,
        marginBottom: 20,
    },
    label: {
        fontSize: 16,
        marginBottom: 8,
    },
    input: {
        fontSize: 16,
        paddingHorizontal: 10,
        marginBottom: 16,
        height: 44,
        borderWidth: 1,
        borderRadius: 8,
        backgroundColor: theme.colors.surfaceElevated,
    },
    message: {
        fontSize: 15,
        marginBottom: 16,
        color: theme.colors.textTitle,
    },
    button: {
        marginTop: 8,
    },
});
